"use client";

import { Users, Phone, Mail, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface GroupSizeExceedsModalProps {
  venueName?: string;
  supportEmail?: string;
  supportPhone?: string;
  onBack: () => void;
  maxGroupSize?: number;
  onRequestSupport?: () => void;
  isOverlay?: boolean;
}

export function GroupSizeExceedsModal({
  venueName,
  supportEmail = "bookings@venue.com",
  supportPhone = "+1 (555) 123-4567",
  onBack,
  maxGroupSize = 7,
  onRequestSupport,
  isOverlay = false,
}: GroupSizeExceedsModalProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const handlePhoneClick = () => {
    if (!supportPhone) return;
    window.location.href = `tel:${supportPhone}`;
  };

  const handleEmailClick = () => {
    if (!supportEmail) return;
    const subject = encodeURIComponent("Large group booking assistance");
    const body = encodeURIComponent(
      "Hello,\n\nI would like help with booking for a larger group.\n\nThank you!"
    );
    window.open(
      `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${supportEmail}&su=${subject}&body=${body}`,
      "_blank"
    );
  };

  return (
    <section className={cn("w-full flex flex-col relative", isOverlay ? "" : "min-h-screen")}>
      <div className={cn("flex items-center justify-center py-8 px-4 md:py-12", isOverlay ? "" : "flex-1")}>
        <div className="w-full max-w-md mx-auto space-y-6">
          <div className="flex justify-center">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm hover:text-primary bg-transparent border-none p-0 outline-none focus:outline-none cursor-pointer"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("booking.group.back", "Back")}
            </button>
          </div>

          <div className="space-y-6 text-center">
            {/* Icon Container */}
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
                <Users className="h-10 w-10 text-orange-500" />
              </div>
            </div>

            {/* Title and Description */}
            <div className="space-y-3">
              <h2 className="text-2xl font-bold">
                {t(
                  "booking.group.exceeds.title",
                  "Group Size Exceeds Online Booking"
                )}
              </h2>
              <p className="text-sm text-amber-700 font-medium">
                {t(
                  "booking.group.exceeds.subtitle",
                  `More than ${maxGroupSize} participants requires special coordination`
                )}
              </p>
            </div>

            {/* Main Message */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t(
                "booking.group.exceeds.description",
                `For groups larger than ${maxGroupSize} participants, our team needs to personally arrange your booking to ensure the best experience and availability.`
              )}
            </p>

            {/* Contact Buttons */}
            <div className="space-y-3 pt-4">
              <Button
                onClick={handlePhoneClick}
                size="lg"
                disabled={!isMobile}
                className={cn(
                  "w-full bg-purple-600 hover:bg-purple-700",
                  !isMobile && "opacity-50 cursor-not-allowed pointer-events-none"
                )}
              >
                <Phone className="h-4 w-4 mr-2" />
                {supportPhone}
              </Button>
              <Button
                onClick={handleEmailClick}
                variant="outline"
                size="lg"
                className="w-full"
              >
                <Mail className="h-4 w-4 mr-2" />
                {supportEmail}
              </Button>
            </div>

            {/* Footer Note */}
            <p className="text-xs text-muted-foreground">
              {t(
                "booking.group.exceeds.note",
                `Online booking is only available for groups of ${maxGroupSize} or fewer participants`
              )}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
